import axios from 'axios';

import { getApiHost } from './getApiHost';
import { extractDownloadFilename } from './extractDownloadFilename';
import { loginRequest, type CompatInstance } from '../auth/firebaseMsalCompat';

/** Output format requested from the backend.
 *
 * - `'standard'` (default): MusicXML 3.0 with fingerings under
 *   `<notations><technical><fingering>`. Works in every major notation
 *   editor except Sibelius, which silently drops them on import.
 * - `'sibelius'`: same MusicXML re-shaped by the backend's
 *   `convert_to_sibelius_fingerings` so Sibelius imports the fingerings
 *   (and partitions the staves) correctly. See `sibelius_converter.py`
 *   for the rationale.
 */
export type DownloadProjectXmlFormat = 'standard' | 'sibelius' | 'plain';

export interface DownloadProjectXmlOptions {
  /** Defaults to `'standard'`. Only sent on the wire when not standard. */
  format?: DownloadProjectXmlFormat;
}

/**
 * Fetch the merged MusicXML for a PDF project and trigger a browser
 * download. Acquires the Firebase ID token silently via the auth
 * instance, streams the response as a Blob (the backend
 * returns the bytes directly — there is no SAS URL round trip or
 * per-download blob artifact), and dispatches a temporary `<a download>`
 * click to kick off the save dialog. Filename is sourced from the
 * `Content-Disposition` header (with a sensible fallback), supporting
 * both the `filename=` and RFC 5987 `filename*=UTF-8''…` forms via
 * `extractDownloadFilename`.
 *
 * Pass `{ format: 'sibelius' }` to request the Sibelius-compatible
 * variant. The backend reads `?format=` and runs the Sibelius
 * converter on the merged bytes; the response shape and filename
 * conventions stay the same on the client side (the converter mutates
 * the suffix to `_fingering_sibelius.xml` and we pick that up from
 * `Content-Disposition` without special-casing).
 *
 * The function is intentionally side-effect-only: any UI state
 * (spinners, error toasts, telemetry) stays on the caller so the util
 * can be reused from other contexts (e.g. CLI debug, retries) without
 * dragging in React.
 */
export const downloadProjectXml = async (
  projectId: string,
  authInstance: CompatInstance,
  options: DownloadProjectXmlOptions = {}
): Promise<void> => {
  const authResult = await authInstance.acquireTokenSilent({
    ...loginRequest,
  });

  const format = options.format ?? 'standard';

  const response = await axios.get<Blob>(
    `${getApiHost()}/project/${projectId}/download-xml`,
    {
      headers: { Authorization: `Bearer ${authResult.idToken}` },
      responseType: 'blob',
      // Only send the query when it's the non-default variant so the
      // network log stays clean for the existing standard flow.
      params: format === 'standard' ? undefined : { format },
    }
  );

  const filename = extractDownloadFilename(
    response.headers['content-disposition'] as string | undefined,
    format === 'sibelius'
      ? 'fingering_sibelius.xml'
      : format === 'plain'
        ? 'score.xml'
        : 'fingering.xml'
  );

  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoke on next tick so Safari/Firefox have time to start the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};
